import type {
  EvoPermissionCheckCallId,
  EvoPermissionCheckGroup,
  EvoPermissionCheckLeftover,
  EvoPermissionCheckResult,
  EvoPermissionCheckStepId,
  EvoPermissionCheckStepStatus,
} from '../../services/firebaseFunctions';
import type { StatusTone } from './EvoStepSection';

/** Teste de permissões do token da EVO (REEDY-141): lógica pura de exibição, sem estado nem rede. */

export const PERMISSION_CHECK_STALE_DAYS = 90;
export const PERMISSION_CHECK_TOTAL_STEPS = 8;

export const PERMISSION_CHECK_GROUP_LABELS: Record<EvoPermissionCheckGroup, string> = {
  webhooks: 'Webhooks',
  sales: 'Vendas',
  members: 'Membros',
};

export interface PermissionCheckStepMeta {
  id: EvoPermissionCheckStepId;
  group: EvoPermissionCheckGroup;
  title: string;
  /** Rota da EVO que a etapa chama (spec, tabela das 8 etapas). */
  route: string;
  /** Chamada ao servidor que executa a etapa. */
  call: EvoPermissionCheckCallId;
}

/** As 8 etapas, na ordem da spec. */
export const PERMISSION_CHECK_STEPS: readonly PermissionCheckStepMeta[] = [
  { id: 'listWebhooks', group: 'webhooks', title: 'Listar webhooks', route: 'GET /api/v1/webhook', call: 'listWebhooks' },
  { id: 'createTestWebhook', group: 'webhooks', title: 'Criar webhook de teste', route: 'POST /api/v1/webhook', call: 'webhookProbe' },
  { id: 'findTestWebhook', group: 'webhooks', title: 'Conferir o webhook de teste na lista', route: 'GET /api/v1/webhook', call: 'webhookProbe' },
  { id: 'deleteTestWebhook', group: 'webhooks', title: 'Excluir o webhook de teste', route: 'DELETE /api/v1/webhook', call: 'webhookProbe' },
  { id: 'listSales', group: 'sales', title: 'Listar vendas', route: 'GET /api/v2/sales', call: 'listSales' },
  { id: 'getSale', group: 'sales', title: 'Dados da venda', route: 'GET /api/v2/sales/:id', call: 'getSale' },
  { id: 'getMember', group: 'members', title: 'Membro da venda (nota fiscal)', route: 'GET /api/v2/members/:id', call: 'getMember' },
  {
    id: 'getMemberWithMemberships',
    group: 'members',
    title: 'Membro com contratos (Reedy 360)',
    route: 'GET /api/v2/members/:id?showMemberships=true',
    call: 'getMemberWithMemberships',
  },
];

export const PERMISSION_CHECK_GROUPS: readonly EvoPermissionCheckGroup[] = ['webhooks', 'sales', 'members'];

const DAY_MS = 24 * 60 * 60 * 1000;

interface StepLike {
  status: EvoPermissionCheckStepStatus;
}

export interface PermissionCheckCounts {
  ok: number;
  failed: number;
  noSample: number;
  notRun: number;
}

export function countSteps(steps: readonly StepLike[]): PermissionCheckCounts {
  return {
    ok: steps.filter((step) => step.status === 'ok').length,
    failed: steps.filter((step) => step.status === 'failed').length,
    noSample: steps.filter((step) => step.status === 'no_sample').length,
    notRun: steps.filter((step) => step.status === 'not_run').length,
  };
}

const joinWithE = (parts: string[]) => (parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`);

/** Resumo curto, por exemplo "7 de 8 OK e 1 sem venda para testar". */
export function summarizeCounts(counts: PermissionCheckCounts): string {
  const parts = [`${counts.ok} de ${PERMISSION_CHECK_TOTAL_STEPS} OK`];
  if (counts.noSample > 0) parts.push(`${counts.noSample} sem venda para testar`);
  if (counts.failed > 0) parts.push(counts.failed === 1 ? '1 falhou' : `${counts.failed} falharam`);
  if (counts.notRun > 0) parts.push(counts.notRun === 1 ? '1 não executado' : `${counts.notRun} não executados`);
  return joinWithE(parts);
}

export const failuresLabel = (failed: number) => (failed === 1 ? '1 falha' : `${failed} falhas`);

export interface StepChip {
  label: string;
  tone: StatusTone;
}

/** Chip de uma etapa com resultado ("Aguardando" para as que ainda não rodaram). */
export function stepChip(
  step: { status: EvoPermissionCheckStepStatus; httpStatus: number | null },
  simulated: boolean,
): StepChip {
  switch (step.status) {
    case 'ok':
      return { label: simulated ? 'OK (simulado)' : 'OK', tone: 'ok' };
    case 'failed':
      return { label: step.httpStatus ? `Falhou · HTTP ${step.httpStatus}` : 'Falhou', tone: 'error' };
    case 'no_sample':
      return { label: 'Sem venda para testar', tone: 'info' };
    case 'not_run':
      return { label: 'Não executado', tone: 'neutral' };
    default:
      return { label: 'Aguardando', tone: 'neutral' };
  }
}

/** "Copiar detalhe" copia só o código HTTP e a mensagem do catálogo (RS-22). */
export function stepDetailText(step: { httpStatus: number | null; message: string | null }): string {
  const message = step.message ?? '';
  return step.httpStatus ? `HTTP ${step.httpStatus} · ${message}` : message;
}

export function formatDateTime(iso: string): { date: string; time: string } | null {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return {
    date: parsed.toLocaleDateString('pt-BR'),
    time: parsed.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
  };
}

export type PermissionCheckStateKind = 'undefined' | 'verified' | 'failed' | 'stale';

export interface PermissionCheckState {
  kind: PermissionCheckStateKind;
  label: string;
  tone: StatusTone;
  detail: string;
  /** "N falhas": a lista por rota abre sozinha. */
  autoOpen: boolean;
  /** Há resultado por rota para abrir com "Ver resultado". */
  hasResult: boolean;
}

/**
 * Estado da etapa 1 a partir do último resultado salvo (J3). Ausente = "Sem definição".
 * Com falhas, "N falhas" prevalece sobre "Teste antigo": a falha é o que precisa de ação.
 */
export function getPermissionCheckState(check: EvoPermissionCheckResult | undefined | null, now: Date = new Date()): PermissionCheckState {
  if (!check) {
    return {
      kind: 'undefined',
      label: 'Sem definição',
      tone: 'neutral',
      detail: 'Este token ainda não foi testado. Teste para saber se ele acessa tudo o que o Reedy usa na EVO.',
      autoOpen: false,
      hasResult: false,
    };
  }

  const when = formatDateTime(check.testedAt);
  const stamp = when ? `Testado em ${when.date} às ${when.time}` : 'Testado';
  const by = check.testedBy?.name ? ` por ${check.testedBy.name}` : '';
  const simulatedNote = check.simulated ? ' Teste simulado: nenhuma chamada real foi feita à EVO.' : '';
  const summary = summarizeCounts(check.summary);

  if (check.summary.failed > 0 || check.outcome === 'failed') {
    return {
      kind: 'failed',
      label: failuresLabel(check.summary.failed),
      tone: 'error',
      detail: `${stamp}${by}. Libere as permissões que faltam no painel da EVO e teste de novo.${simulatedNote}`,
      autoOpen: true,
      hasResult: true,
    };
  }

  const testedAtMs = new Date(check.testedAt).getTime();
  const ageDays = Number.isNaN(testedAtMs) ? 0 : (now.getTime() - testedAtMs) / DAY_MS;
  if (ageDays > PERMISSION_CHECK_STALE_DAYS) {
    return {
      kind: 'stale',
      label: 'Teste antigo',
      tone: 'warning',
      detail: `Testado há ${Math.floor(ageDays)} dias${when ? ` (${when.date})` : ''}${by} · ${summary}. As permissões podem ter mudado na EVO; teste de novo.${simulatedNote}`,
      autoOpen: false,
      hasResult: true,
    };
  }

  return {
    kind: 'verified',
    label: 'Verificadas',
    tone: 'ok',
    detail: `${stamp}${by} · ${summary}.${simulatedNote}`,
    autoOpen: false,
    hasResult: true,
  };
}

export interface LeftoverNoticeView {
  title: string;
  body: string;
  evoIds: number[];
  marker: string;
  /** Texto de cada botão de copiar e o que ele copia: só ID(s) da EVO ou o marcador (RS-22). */
  copyActions: Array<{ label: string; text: string }>;
  registeredAt: string | null;
}

const TESTED_ACCOUNT_NOTE = 'Se o teste usou credenciais ainda não salvas, ele pode estar na conta testada, e não na conta salva neste cadastro.';

/** DNS da conta EVO para exibição: 4 primeiros caracteres + `***`. Nunca exibir o DNS completo. */
export const maskDns = (dns: string) => (!dns || dns.length < 4 ? dns : `${dns.substring(0, 4)}***`);

/**
 * Onde está o webhook de teste, quando a sobra traz o DNS da conta testada.
 * A comparação com o DNS salvo ignora espaços e caixa (a mesma conta EVO aparece com caixas diferentes) e nunca usa as versões mascaradas.
 * Na mesma conta, exibe o DNS salvo mascarado (o mesmo que a tela mostra no cadastro).
 */
function leftoverAccount(
  leftoverDns: string | undefined,
  savedDns: string | undefined | null,
): { where: string; apagueLa: boolean; comma: string } | null {
  if (!leftoverDns) return null;
  if (savedDns && leftoverDns.trim().toLowerCase() === savedDns.trim().toLowerCase()) {
    return { where: `na conta EVO deste cadastro (${maskDns(savedDns)})`, apagueLa: false, comma: '' };
  }
  const tested = `na conta EVO testada (${maskDns(leftoverDns)})`;
  return {
    where: savedDns
      ? `${tested}, que não é a conta salva neste cadastro (${maskDns(savedDns)})`
      : `${tested}, cujas credenciais não foram salvas neste cadastro`,
    apagueLa: true,
    comma: ',',
  };
}

/** Frase do aviso do diálogo de teste com credenciais digitadas: o DNS testado vem da sobra ou, na falta, do digitado. */
export function testedAccountSentence(dns: string | undefined | null): string | null {
  if (!dns) return null;
  return `Ele está na conta EVO testada (${maskDns(dns)}), que pode não ser a conta salva neste cadastro. Apague-o lá.`;
}

/** Aviso "Webhook de teste não apagado": existe quando o cadastro tem `permissionCheckLeftover`, em qualquer estado da verificação. */
export function getLeftoverNotice(
  leftover: EvoPermissionCheckLeftover | undefined | null,
  savedDns?: string | null,
): LeftoverNoticeView | null {
  if (!leftover) return null;
  const evoIds = Array.isArray(leftover.evoIds) ? leftover.evoIds : [];
  const when = leftover.at ? formatDateTime(leftover.at) : null;
  const registeredAt = when ? `Registrado em ${when.date} às ${when.time}` : null;
  const ids = joinWithE(evoIds.map(String));
  const account = leftoverAccount(leftover.dns, savedDns);

  if (evoIds.length === 0) {
    const body = account
      ? `O teste pode ter criado um webhook de teste ${account.where}, e o Reedy não conseguiu obter o ID nem confirmar a remoção. ${account.apagueLa ? 'Nessa conta' : 'Na EVO'}, procure um webhook cuja URL contenha ${leftover.marker} e apague-o.`
      : `O teste pode ter criado um webhook de teste na conta EVO, e o Reedy não conseguiu obter o ID nem confirmar a remoção. Na EVO, procure um webhook cuja URL contenha ${leftover.marker} e apague-o. Ele pode estar na conta testada, e não na conta salva neste cadastro.`;
    return {
      title: 'Webhook de teste não apagado',
      body,
      evoIds,
      marker: leftover.marker,
      copyActions: [{ label: 'Copiar marcador', text: leftover.marker }],
      registeredAt,
    };
  }

  if (evoIds.length === 1) {
    const body = account
      ? `O teste criou o webhook de teste (ID ${ids}) ${account.where}${account.comma} e o Reedy não conseguiu confirmar que ele foi apagado. Ele não dispara e não afeta os webhooks do Reedy. ${account.apagueLa ? 'Apague-o lá.' : 'Apague-o na EVO.'}`
      : `O teste criou o webhook de teste (ID ${ids}) na conta EVO e o Reedy não conseguiu confirmar que ele foi apagado. Ele não dispara e não afeta os webhooks do Reedy. Apague-o na EVO. ${TESTED_ACCOUNT_NOTE}`;
    return {
      title: 'Webhook de teste não apagado',
      body,
      evoIds,
      marker: leftover.marker,
      copyActions: [{ label: 'Copiar ID', text: String(evoIds[0]) }],
      registeredAt,
    };
  }

  const body = account
    ? `Testes criaram webhooks de teste (IDs ${ids}) ${account.where}${account.comma} e o Reedy não conseguiu confirmar que eles foram apagados. Eles não disparam e não afetam os webhooks do Reedy. ${account.apagueLa ? 'Apague-os lá.' : 'Apague-os na EVO.'}`
    : `Testes criaram webhooks de teste na conta EVO (IDs ${ids}) e o Reedy não conseguiu confirmar que eles foram apagados. Eles não disparam e não afetam os webhooks do Reedy. Apague-os na EVO. ${TESTED_ACCOUNT_NOTE}`;
  return {
    title: `${evoIds.length} webhooks de teste não apagados`,
    body,
    evoIds,
    marker: leftover.marker,
    copyActions: evoIds.map((evoId) => ({ label: `Copiar ${evoId}`, text: String(evoId) })),
    registeredAt,
  };
}
